import { FC, Fragment } from "react";
import SubscriptionBannerAdvertisement from "@shared/components/subscription-advertisement";
import InfintieScrollLoader from "@svg/infiniteScrollLoader";
import LandingProductLoading from "./loading";
import useInfiniteScroll from "../hooks/useInfiniteScroll";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import HowToLearnProductCard from "./howToLearnProduct";
import {
  Pagination,
  PAGINATION_BUTTON_VARIANT,
} from "@shared/components/pagination";
import { HOW_TO_LEARN_PAGE_LIMIT } from "../helpers/constants";

const MAX_INFINITE_SCROLL_PAGES = 4;
const MOBILE_BANNER_INDEX = 9;
const DESKTOP_BANNER_INDEX = 11;

const InfiniteScrollResult: FC = () => {
  const {
    productRef,
    collection,
    pageNum,
    paginationMode,
    loading,
    setLastElement,
    data,
    isMobile,
    totalPage,
    handleClickOnPage,
    productNumber,
  } = useInfiniteScroll();

  const isTablet = useIsMobile(1024, true);

  const hasMore = data?.pagination?.remaining_pages !== 0;
  const isInfiniteScrollMode =
    !paginationMode && pageNum <= MAX_INFINITE_SCROLL_PAGES;

  const shouldShowPagination =
    paginationMode || (pageNum > MAX_INFINITE_SCROLL_PAGES && hasMore);

  const shouldShowScrollLoader = isInfiniteScrollMode && loading;

  const bannerIndex = isTablet ? MOBILE_BANNER_INDEX : DESKTOP_BANNER_INDEX;

  const lastItemIndex = collection.length - 1;

  return (
    <div className="mb-6">
      <div
        ref={productRef}
        className="flex w-full pb-6  flex-wrap md:gap-6 gap-3.5 lg:grid lg:grid-cols-2"
      >
        {collection.map((course, index) => {
          const shouldObserveLastItem =
            isInfiniteScrollMode &&
            hasMore &&
            !loading &&
            index === lastItemIndex;

          return (
            <Fragment key={course.id}>
              <div
                ref={shouldObserveLastItem ? setLastElement : null}
                className="relative w-full lg:flex-1"
              >
                <div className="absolute -top-3 -right-3 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-fds-brand-light md:-top-3.5 md:-right-3.5 md:h-7 md:w-7 lg:hidden">
                  <p className="body-md text-fds-secondary">
                    {productNumber(index)}
                  </p>
                </div>

                <HowToLearnProductCard
                  {...course}
                  title={course.title_short || course.title}
                  student_count={course.student_count ?? 0}
                  hasCertificate={
                    course.hasCertificate || course.has_certificate
                  }
                />
              </div>

              {(index === bannerIndex ||
                (lastItemIndex < bannerIndex && index === lastItemIndex)) && (
                <SubscriptionBannerAdvertisement
                  type="howToLearn"
                  className="col-span-2"
                />
              )}
            </Fragment>
          );
        })}

        {loading && hasMore && (
          <LandingProductLoading count={HOW_TO_LEARN_PAGE_LIMIT} />
        )}
      </div>

      {shouldShowScrollLoader && (
        <InfintieScrollLoader className="justify-self-center" />
      )}

      {shouldShowPagination && (
        <div className="col-span-2 md:mt-0 lg:mt-6 mt-6 flex w-full justify-center">
          <Pagination
            currentPage={pageNum}
            totalPages={totalPage}
            pageItemsToShow={3}
            onPageChange={handleClickOnPage}
            variant={
              isMobile
                ? PAGINATION_BUTTON_VARIANT.ARROW
                : PAGINATION_BUTTON_VARIANT.TEXT
            }
          />
        </div>
      )}
    </div>
  );
};

export default InfiniteScrollResult;
